import type { Map as LeafletMap, Marker } from 'leaflet';

/**
 * Mapa de sucursales de la landing de agencia (/agencia-alquiler/{rentadora}/, typePage 10):
 * selects País → Ciudad → Sucursal, card con dirección/horario/teléfono y mapa Leaflet.
 * Compartido por desktop (src/ts/landing.ts) y mobile (mobile/src/ts/landing.ts): usa DOM nativo
 * para no depender de jQuery ni de Dom7. Markup: views/pages/inc/sucursales-agencia.blade.php y
 * views/mobile/pages/inc/sucursales-agencia-mobile.blade.php (mismos data-sa).
 */

/** Sucursal tal como la entrega JsonLiviano::sucursalesSlim (horario ya en el idioma de la página) */
export interface SucursalAgencia {
	idOffice: number;
	nameOffice: string;
	addressOffice: string;
	telephoneOffice?: string;
	latitude?: number;
	longitude?: number;
	idRent: number;
	nameRent: string;
	idCity?: number;
	nameCity?: string;
	idAirport?: number;
	nameAirport?: string;
	idCountry?: number;
	nameCountry?: string;
	imgRentMap?: string;
	openingHours?: Record<string, { idDay: number; nameDay: string; hours: { opens: string; closes: string }[] }>;
}

interface Textos {
	cerrado: string;
	verOfertas: string;
	tituloPopup: string;
}

export function initSucursalesAgencia(contenedor: HTMLElement | null, onVerOfertas: (sucursal: SucursalAgencia) => void): void {
	if (!contenedor) return;
	const root: HTMLElement = contenedor;

	// JSON plano en un <script type="application/json"> (ver views/pages/inc/sucursales-agencia.blade.php),
	// no comprimido con LZString: con rentadoras grandes (Hertz ~2.800 sucursales) comprimir en PHP
	// tardaba >1s de CPU por visita Y pesaba más después de gzip que el JSON plano sin comprimir.
	const data = parseData<{ offices: SucursalAgencia[]; textos: Textos }>(document.getElementById('sa-data')?.textContent);
	const sucursales: SucursalAgencia[] = data?.offices ?? [];
	const textos: Textos = data?.textos ?? { cerrado: 'Cerrado', verOfertas: 'Ver ofertas', tituloPopup: '' };
	if (!sucursales.length) return;

	// idOffice repite entre integraciones (ej. varias en 0) — el grupo de sucursales junta varios
	// ID_MEMPRESA en un mismo mapa (ver getGrupoLandingAgencia), así que la clave real es la pareja.
	const claveOffice = (s: SucursalAgencia) => `${s.idRent}|${s.idOffice}`;

	const $ = <T extends HTMLElement>(name: string) => root.querySelector(`[data-sa="${name}"]`) as T;
	const selPais = $<HTMLSelectElement>('pais');
	const selCiudad = $<HTMLSelectElement>('ciudad');
	const selSucursal = $<HTMLSelectElement>('sucursal');
	const card = $<HTMLElement>('card');
	const elMapa = $<HTMLElement>('mapa');

	let mapa: LeafletMap | undefined;
	let visibles: SucursalAgencia[] = sucursales;
	const marcadores = new Map<string, Marker>();

	selPais.addEventListener('change', procesarPais);

	// El país puede llegar preseleccionado desde el blade (Colombia si la rentadora tiene sucursales
	// ahí): arranca con ese filtro ya aplicado, en vez de con el mapa mostrando todos los países del
	// grupo a la vez — mismo código que si el visitante lo hubiera elegido a mano.
	if (selPais.value) procesarPais();

	function procesarPais() {
		const ids = sucursales.filter(s => String(s.idCountry) === selPais.value);
		llenarSelect(selCiudad, unicos(ids, s => s.idCity, s => s.nameCity ?? ''));
		llenarSelect(selSucursal, []);
		ocultarCard();
		enfocar(selPais.value ? ids : sucursales);
	}

	selCiudad.addEventListener('change', () => {
		const ids = sucursales.filter(s => String(s.idCountry) === selPais.value && String(s.idCity) === selCiudad.value);
		llenarSelect(selSucursal, ids.map(s => ({ value: claveOffice(s), label: s.addressOffice || s.nameOffice })));
		ocultarCard();
		enfocar(selCiudad.value ? ids : sucursales.filter(s => String(s.idCountry) === selPais.value));
	});

	selSucursal.addEventListener('change', () => {
		const sucursal = sucursales.find(s => claveOffice(s) === selSucursal.value);
		if (!sucursal) {
			ocultarCard();
			enfocar(sucursales.filter(s => String(s.idCity) === selCiudad.value));
			return;
		}
		mostrarCard(sucursal);
		enfocar([sucursal]);
		marcadores.get(claveOffice(sucursal))?.openPopup();
	});

	$<HTMLElement>('ver-ofertas').addEventListener('click', () => {
		const sucursal = sucursales.find(s => claveOffice(s) === selSucursal.value);
		if (sucursal) onVerOfertas(sucursal);
	});

	// Leaflet (~150KB) solo se descarga cuando el bloque está por verse
	if ('IntersectionObserver' in window) {
		const observer = new IntersectionObserver((entries) => {
			if (entries.some(e => e.isIntersecting)) {
				observer.disconnect();
				cargarMapa();
			}
		}, { rootMargin: '200px' });
		observer.observe(elMapa);
	} else {
		cargarMapa();
	}

	/** Clic en un marcador: deja los 3 selects en esa sucursal, como si se hubiera elegido a mano */
	function seleccionar(sucursal: SucursalAgencia) {
		selPais.value = String(sucursal.idCountry);
		llenarSelect(selCiudad, unicos(sucursales.filter(s => s.idCountry === sucursal.idCountry), s => s.idCity, s => s.nameCity ?? ''));
		selCiudad.value = String(sucursal.idCity);
		llenarSelect(selSucursal, sucursales
			.filter(s => s.idCountry === sucursal.idCountry && s.idCity === sucursal.idCity)
			.map(s => ({ value: claveOffice(s), label: s.addressOffice || s.nameOffice })));
		selSucursal.value = claveOffice(sucursal);
		mostrarCard(sucursal);
	}

	function mostrarCard(s: SucursalAgencia) {
		$<HTMLElement>('direccion').textContent = s.addressOffice || s.nameOffice;

		const tbody = $<HTMLElement>('horario');
		tbody.replaceChildren(...Object.values(s.openingHours ?? {})
			// Lunes (2) a sábado (7), domingo (1) y festivos (8)
			.sort((a, b) => (a.idDay === 1 ? 7.5 : a.idDay) - (b.idDay === 1 ? 7.5 : b.idDay))
			.map(dia => {
				const tr = document.createElement('tr');
				const tdDia = document.createElement('td');
				const tdHoras = document.createElement('td');
				tdDia.textContent = dia.nameDay;
				tdHoras.textContent = dia.hours?.length ? dia.hours.map(h => `${h.opens} - ${h.closes}`).join(' / ') : textos.cerrado;
				if (!dia.hours?.length) tdHoras.classList.add('cerrado');
				tr.append(tdDia, tdHoras);
				return tr;
			}));
		$<HTMLElement>('bloque-horario').hidden = !tbody.children.length;

		card.hidden = false;
	}

	function ocultarCard() {
		card.hidden = true;
	}

	/**
	 * Ajusta el mapa a esas sucursales (si todavía no cargó, lo aplica al cargar).
	 *
	 * ponytail: descarta puntos lejos de la mediana antes de calcular el encuadre — algunas
	 * sucursales (de varias integraciones, incluso datos "propios") traen coordenadas erróneas
	 * (ej. longitud con el signo invertido, cae del otro lado del mundo) y una sola de esas
	 * estira el zoom inicial hasta abarcar medio planeta. No se ocultan ni se corrigen esos puntos
	 * (siguen con su marcador normal en el mapa, seleccionables igual) — solo no cuentan para
	 * decidir el encuadre de arranque. Techo: umbral fijo de 15°; si algún país real resulta más
	 * ancho que eso (ej. Brasil de punta a punta), subir el umbral o calcularlo por país.
	 */
	function enfocar(lista: SucursalAgencia[]) {
		visibles = lista.length ? lista : sucursales;
		if (!mapa) return;
		const puntos = puntosSinOutliers(visibles);
		if (puntos.length === 1) mapa.setView(puntos[0], 15);
		else if (puntos.length > 1) mapa.fitBounds(puntos, { padding: [40, 40], maxZoom: 13 });
	}

	async function cargarMapa() {
		try {
			await import('leaflet/dist/leaflet.css');
			const L = await import('leaflet');

			// Centro/zoom de arranque por si `enfocar()` no encuentra ningún punto válido (todas las
			// coordenadas de esta rentadora inválidas/con outliers) — sin esto el mapa queda gris,
			// sin tiles, porque Leaflet nunca pide nada hasta el primer setView/fitBounds.
			mapa = L.map(elMapa, { scrollWheelZoom: true, zoomControl: true, center: [4.5709, -74.2973], zoom: 5 });
			L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
				maxZoom: 19,
				minZoom: 3,
				detectRetina: true,
			}).addTo(mapa);

			sucursales.filter(tieneCoordenadas).forEach(s => {
				const icono = L.divIcon({
					className: 'icon-marker',
					html: s.imgRentMap ? `<img src="${encodeURI(s.imgRentMap)}" width="40" height="40" alt="">` : '',
					iconSize: [40, 40],
					iconAnchor: [20, 40],
					popupAnchor: [0, -40],
				});
				const marcador = L.marker(coordenadas(s), { icon: icono })
					.addTo(mapa!)
					.bindPopup(() => popup(s), { maxWidth: 240 })
					.on('click', () => seleccionar(s));
				marcadores.set(claveOffice(s), marcador);
			});

			enfocar(visibles);
			root.querySelector('[data-sa="cargando"]')?.remove();
		} catch (error) {
			console.error('Error cargando el mapa de sucursales', error);
		}
	}

	/** Globo del marcador armado con textContent (direcciones y teléfonos vienen de APIs externas) */
	function popup(s: SucursalAgencia): HTMLElement {
		const div = document.createElement('div');
		div.className = 'sa-popup';
		const titulo = document.createElement('strong');
		titulo.textContent = `${textos.tituloPopup} ${s.nameRent}`.trim();
		const direccion = document.createElement('span');
		direccion.textContent = s.addressOffice || s.nameOffice;
		div.append(titulo, direccion);
		const boton = document.createElement('button');
		boton.type = 'button';
		boton.className = 'sa-popup-ofertas';
		boton.textContent = textos.verOfertas;
		boton.addEventListener('click', () => onVerOfertas(s));
		div.append(boton);
		return div;
	}
}

function parseData<T>(raw: string | undefined | null): T | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function tieneCoordenadas(s: SucursalAgencia): boolean {
	return Number.isFinite(Number(s.latitude)) && Number.isFinite(Number(s.longitude)) && !(Number(s.latitude) === 0 && Number(s.longitude) === 0);
}

/** Las coordenadas pueden venir como string desde las APIs de las rentadoras */
function coordenadas(s: SucursalAgencia): [number, number] {
	return [Number(s.latitude), Number(s.longitude)];
}

/** Umbral de distancia a la mediana (en grados) para descartar un punto del cálculo de encuadre */
const UMBRAL_OUTLIER_GRADOS = 15;

/** Coordenadas de la lista, sin las que están a más de UMBRAL_OUTLIER_GRADOS de la mediana (ver enfocar()) */
function puntosSinOutliers(lista: SucursalAgencia[]): [number, number][] {
	const puntos = lista.filter(tieneCoordenadas).map(coordenadas);
	if (puntos.length < 3) return puntos; // sin suficientes puntos para que la mediana signifique algo
	const medianaLat = mediana(puntos.map(p => p[0]));
	const medianaLng = mediana(puntos.map(p => p[1]));
	const filtrados = puntos.filter(([lat, lng]) => Math.abs(lat - medianaLat) <= UMBRAL_OUTLIER_GRADOS && Math.abs(lng - medianaLng) <= UMBRAL_OUTLIER_GRADOS);
	return filtrados.length ? filtrados : puntos; // si el filtro dejara la lista vacía, mejor mostrar todo que nada
}

function mediana(valores: number[]): number {
	const ordenados = [...valores].sort((a, b) => a - b);
	const mitad = Math.floor(ordenados.length / 2);
	return ordenados.length % 2 ? ordenados[mitad] : (ordenados[mitad - 1] + ordenados[mitad]) / 2;
}

function unicos(lista: SucursalAgencia[], id: (s: SucursalAgencia) => unknown, nombre: (s: SucursalAgencia) => string) {
	const vistos = new Map<string, string>();
	lista.forEach(s => {
		const key = String(id(s) ?? '');
		if (key && !vistos.has(key)) vistos.set(key, nombre(s));
	});
	return [...vistos].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
}

/** Deja solo el placeholder (primera opción) y agrega las opciones; sin opciones queda deshabilitado */
function llenarSelect(select: HTMLSelectElement, opciones: { value: string; label: string }[]) {
	const placeholder = select.options[0];
	select.replaceChildren(placeholder);
	opciones.forEach(o => select.add(new Option(o.label, o.value)));
	select.value = '';
	select.disabled = !opciones.length;
}
